// Fisheye geometry shared by every browser dewarp (Monitoring look-around,
// dewarped AI view tiles, the Settings calibration and view editor).
//
// It is the same model DeepStream's dewarp stage is configured from
// (services/deepstream/src/blinkfree_deepstream/dewarp.py), so a view framed
// here is the view the detector analyses.
//
// World frame: X = yaw 0, Y = yaw 90, Z up. A view is a virtual pinhole camera
// at the lens centre: yaw about vertical, tilt in degrees below the horizon
// (negative looks up), fov = vertical field of view.

export type LensModel = "equisolid" | "equidistant" | "stereographic" | "orthographic";
export type Mount = "ceiling" | "wall" | "floor";

/** A fisheye's lens and installation. The image circle is in fractions of
 *  the frame (cx of the width, cy and r of the height) so it holds across
 *  stream resolutions, and is null until the lens has been calibrated. */
export type Lens = {
  model: LensModel;
  theta_max_deg: number;
  cx: number | null;
  cy: number | null;
  r: number | null;
  mount: Mount;
  roll_deg: number;
};

/** A lens whose image circle is known: everything a dewarp needs. */
export type CalibratedLens = Lens & { cx: number; cy: number; r: number };

export function isCalibrated(lens: Lens | null | undefined): lens is CalibratedLens {
  return Boolean(lens && lens.cx && lens.cy && lens.r);
}

export type Pose = { yaw: number; tilt: number; fov: number };

export type Circle = { cx: number; cy: number; r: number };

export type Vec3 = [number, number, number];

export const DEG = Math.PI / 180;

export const LENS_MODELS: LensModel[] = ["equisolid", "equidistant", "stereographic", "orthographic"];

export const DEFAULT_LENS: Lens = {
  model: "equisolid",
  theta_max_deg: 90,
  cx: null,
  cy: null,
  r: null,
  mount: "ceiling",
  roll_deg: 0,
};

const FRAMES: Record<Mount, { axis: Vec3; ex: Vec3; ey: Vec3 }> = {
  ceiling: { axis: [0, 0, -1], ex: [1, 0, 0], ey: [0, -1, 0] },
  floor: { axis: [0, 0, 1], ex: [1, 0, 0], ey: [0, 1, 0] },
  wall: { axis: [1, 0, 0], ex: [0, -1, 0], ey: [0, 0, -1] },
};

export const MODEL_INDEX: Record<LensModel, number> = {
  equidistant: 0,
  equisolid: 1,
  stereographic: 2,
  orthographic: 3,
};

export function circlePx(lens: CalibratedLens, width: number, height: number): Circle {
  return { cx: lens.cx * width, cy: lens.cy * height, r: lens.r * height };
}

export function circleFraction(c: Circle, width: number, height: number) {
  return { cx: c.cx / width, cy: c.cy / height, r: c.r / height };
}

export const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

function g(theta: number, model: LensModel): number {
  if (model === "equisolid") return Math.sin(theta / 2);
  if (model === "stereographic") return Math.tan(theta / 2);
  if (model === "orthographic") return Math.sin(Math.min(theta, Math.PI / 2));
  return theta;
}

function gInverse(n: number, model: LensModel, thetaMax: number): number {
  const v = n * g(thetaMax, model);
  if (model === "equisolid") return 2 * Math.asin(Math.min(1, v));
  if (model === "stereographic") return 2 * Math.atan(v);
  if (model === "orthographic") return Math.asin(Math.min(1, v));
  return v;
}

/** Optical axis, image-right and image-down in world coordinates. */
export function lensFrame(lens: Lens): { axis: Vec3; ex: Vec3; ey: Vec3 } {
  const m = FRAMES[lens.mount];
  const c = Math.cos(lens.roll_deg * DEG);
  const s = Math.sin(lens.roll_deg * DEG);
  return {
    axis: m.axis,
    ex: [0, 1, 2].map((k) => c * m.ex[k] + s * m.ey[k]) as Vec3,
    ey: [0, 1, 2].map((k) => -s * m.ex[k] + c * m.ey[k]) as Vec3,
  };
}

export function basis(view: Pose): { f: Vec3; r: Vec3; u: Vec3 } {
  const y = view.yaw * DEG;
  const t = view.tilt * DEG;
  const f: Vec3 = [Math.cos(t) * Math.cos(y), Math.cos(t) * Math.sin(y), -Math.sin(t)];
  const r: Vec3 = [Math.sin(y), -Math.cos(y), 0];
  return { f, r, u: cross(r, f) };
}

export function dirToPx(d: Vec3, lens: Lens, c: Circle) {
  const L = lensFrame(lens);
  const len = Math.hypot(d[0], d[1], d[2]);
  const theta = Math.acos(Math.max(-1, Math.min(1, dot(d, L.axis) / len)));
  const x = dot(d, L.ex);
  const y = dot(d, L.ey);
  const h = Math.hypot(x, y);
  const rho = (c.r * g(theta, lens.model)) / g(lens.theta_max_deg * DEG, lens.model);
  return {
    x: c.cx + (h > 1e-9 ? x / h : 1) * rho,
    y: c.cy + (h > 1e-9 ? y / h : 0) * rho,
    theta,
  };
}

export function pxToDir(x: number, y: number, lens: Lens, c: Circle): Vec3 {
  const L = lensFrame(lens);
  const dx = x - c.cx;
  const dy = y - c.cy;
  const rho = Math.hypot(dx, dy);
  const theta = gInverse(rho / c.r, lens.model, lens.theta_max_deg * DEG);
  const ux = rho > 1e-9 ? dx / rho : 1;
  const uy = rho > 1e-9 ? dy / rho : 0;
  const st = Math.sin(theta);
  const ct = Math.cos(theta);
  return [0, 1, 2].map((k) => st * (ux * L.ex[k] + uy * L.ey[k]) + ct * L.axis[k]) as Vec3;
}

export const wrapYaw = (y: number) => ((((y + 180) % 360) + 360) % 360) - 180;
export const clampFov = (f: number) => Math.max(10, Math.min(140, f));

export function clampTilt(lens: Lens, t: number): number {
  const slack = lens.theta_max_deg - 90 + 15;
  if (lens.mount === "ceiling") return Math.max(-slack, Math.min(90, t));
  if (lens.mount === "floor") return Math.max(-90, Math.min(slack, t));
  return Math.max(-89, Math.min(89, t));
}

export function aimAt(lens: Lens, view: Pose, d: Vec3): Pose {
  const len = Math.hypot(d[0], d[1], d[2]);
  return {
    ...view,
    yaw: wrapYaw(Math.atan2(d[1], d[0]) / DEG),
    tilt: clampTilt(lens, Math.asin(Math.max(-1, Math.min(1, -d[2] / len))) / DEG),
  };
}

/** Largest angle off the lens axis anywhere on a view's frame edge. */
export function edgeTheta(lens: Lens, view: Pose, aspect: number): number {
  const b = basis(view);
  const { axis } = lensFrame(lens);
  const th = Math.tan((view.fov * DEG) / 2);
  let worst = 0;
  const N = 12;
  for (let i = 0; i <= N; i++) {
    const s = -1 + (2 * i) / N;
    for (const [nx, ny] of [
      [s, 1],
      [s, -1],
      [1, s],
      [-1, s],
    ]) {
      const d = [0, 1, 2].map((k) => b.f[k] + nx * th * aspect * b.r[k] + ny * th * b.u[k]) as Vec3;
      const c = dot(d, axis) / Math.hypot(d[0], d[1], d[2]);
      worst = Math.max(worst, Math.acos(Math.max(-1, Math.min(1, c))));
    }
  }
  return worst;
}

/** True when the whole view frame stays inside the lens (no black padding). */
export const fitsLens = (lens: Lens, view: Pose, aspect: number) =>
  edgeTheta(lens, view, aspect) <= lens.theta_max_deg * DEG + 1e-6;

function lerpPose(a: Pose, b: Pose, t: number): Pose {
  const dy = ((((b.yaw - a.yaw) % 360) + 540) % 360) - 180;
  return { yaw: wrapYaw(a.yaw + dy * t), tilt: a.tilt + (b.tilt - a.tilt) * t, fov: a.fov + (b.fov - a.fov) * t };
}

/** Move toward `want`, stopping where the frame would leave the lens. */
export function moveWithinLens(lens: Lens, from: Pose, want: Pose, aspect: number): Pose {
  if (fitsLens(lens, want, aspect)) return want;
  if (!fitsLens(lens, from, aspect)) return settle(lens, want, aspect);
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 14; i++) {
    const mid = (lo + hi) / 2;
    if (fitsLens(lens, lerpPose(from, want, mid), aspect)) lo = mid;
    else hi = mid;
  }
  return lerpPose(from, want, lo);
}

/** Bring a view that sees past the lens edge back inside: turn toward the
 *  axis keeping its zoom, and zoom in only if even the axis cannot fit it. */
export function settle(lens: Lens, view: Pose, aspect: number): Pose {
  if (fitsLens(lens, view, aspect)) return view;
  const axisPose =
    lens.mount === "ceiling"
      ? { yaw: view.yaw, tilt: 90 }
      : lens.mount === "floor"
        ? { yaw: view.yaw, tilt: -90 }
        : { yaw: 0, tilt: 0 };
  const target: Pose = { ...axisPose, fov: view.fov };
  if (!fitsLens(lens, target, aspect)) {
    let lo = 10;
    let hi = view.fov;
    for (let i = 0; i < 14; i++) {
      const mid = (lo + hi) / 2;
      if (fitsLens(lens, { ...target, fov: mid }, aspect)) lo = mid;
      else hi = mid;
    }
    target.fov = lo;
  }
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 14; i++) {
    const mid = (lo + hi) / 2;
    if (fitsLens(lens, lerpPose(view, target, mid), aspect)) hi = mid;
    else lo = mid;
  }
  return lerpPose(view, target, hi);
}

// ------------------------------------------------------------------ presets

export type ViewPreset = {
  id: string;
  label: string;
  /** Views of the preset, each with the name it gets as an AI camera. Only
   *  the mount matters, so a preset can be chosen before calibration. */
  views: (lens: Pick<Lens, "mount">) => Array<Pose & { name: string }>;
};

/** Whether a saved view is (within rounding) one of the preset poses. */
export function samePose(a: Pose, b: Pose): boolean {
  return (
    Math.abs(wrapYaw(a.yaw - b.yaw)) < 0.05 &&
    Math.abs(a.tilt - b.tilt) < 0.05 &&
    Math.abs(a.fov - b.fov) < 0.05
  );
}

/** Four-view layouts offered for a fisheye. Poses are for a ceiling mount;
 *  floor mirrors the tilt, and a wall lens gets a forward-facing fan. */
export const VIEW_PRESETS: ViewPreset[] = [
  {
    id: "headings",
    label: "Four headings",
    views: (lens) =>
      lens.mount === "wall"
        ? [
            { name: "Left", yaw: 50, tilt: 10, fov: 70 },
            { name: "Ahead", yaw: 0, tilt: 10, fov: 70 },
            { name: "Right", yaw: -50, tilt: 10, fov: 70 },
            { name: "Below", yaw: 0, tilt: 55, fov: 70 },
          ]
        : [0, 90, 180, -90].map((yaw, i) => ({
            name: `Heading ${[0, 90, 180, 270][i]}°`,
            yaw,
            tilt: lens.mount === "floor" ? -35 : 35,
            fov: 75,
          })),
  },
  {
    // The camera's own four PTZ views (fitted from its native channels):
    // 26 degrees off the lens axis, 97.6 degree vertical FOV at 16:9.
    id: "camera",
    label: "Camera PTZ layout",
    views: (lens) =>
      [90.1, 115.3, -90.1, -65.1].map((yaw, i) => ({
        name: `PTZ ${i + 1}`,
        yaw,
        tilt: lens.mount === "floor" ? -64 : 64,
        fov: 97.6,
      })),
  },
];

// -------------------------------------------------------- circle detection

/** Find the image circle: edge points from each row/column's first and last
 *  lit pixel, then a RANSAC circle so burned-in OSD text outside the disk
 *  cannot drag the fit. Returns pixels of the given source size. */
export function detectCircle(
  src: CanvasImageSource,
  width: number,
  height: number,
): (Circle & { inliers: number }) | null {
  const k = Math.max(1, Math.round(Math.max(width, height) / 1000));
  const w = Math.round(width / k);
  const h = Math.round(height / k);
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const ctx = cv.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(src, 0, 0, w, h);
  const px = ctx.getImageData(0, 0, w, h).data;
  const lit = (x: number, y: number) => {
    const i = (y * w + x) * 4;
    return 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2] > 12;
  };
  const pts: Array<[number, number]> = [];
  for (let y = 0; y < h; y++) {
    let a = -1;
    let b = -1;
    for (let x = 0; x < w; x++)
      if (lit(x, y)) {
        if (a < 0) a = x;
        b = x;
      }
    if (a >= 0 && b - a > 5) pts.push([a, y], [b, y]);
  }
  for (let x = 0; x < w; x++) {
    let a = -1;
    let b = -1;
    for (let y = 0; y < h; y++)
      if (lit(x, y)) {
        if (a < 0) a = y;
        b = y;
      }
    if (a >= 0 && b - a > 5) pts.push([x, a], [x, b]);
  }
  if (pts.length < 30) return null;
  const through3 = (p: number[], q: number[], r: number[]): [number, number, number] | null => {
    const ax = 2 * (q[0] - p[0]);
    const ay = 2 * (q[1] - p[1]);
    const bx = 2 * (r[0] - p[0]);
    const by = 2 * (r[1] - p[1]);
    const det = ax * by - ay * bx;
    if (Math.abs(det) < 1e-9) return null;
    const c1 = q[0] ** 2 + q[1] ** 2 - p[0] ** 2 - p[1] ** 2;
    const c2 = r[0] ** 2 + r[1] ** 2 - p[0] ** 2 - p[1] ** 2;
    const cx = (c1 * by - ay * c2) / det;
    const cy = (ax * c2 - c1 * bx) / det;
    return [cx, cy, Math.hypot(p[0] - cx, p[1] - cy)];
  };
  let best: [number, number, number] | null = null;
  let bestCount = 0;
  const minR = 0.2 * Math.min(w, h);
  const maxR = 0.75 * Math.max(w, h);
  const pick = () => pts[(Math.random() * pts.length) | 0];
  for (let it = 0; it < 3000; it++) {
    const c = through3(pick(), pick(), pick());
    if (!c || c[2] < minR || c[2] > maxR) continue;
    let n = 0;
    for (const p of pts) if (Math.abs(Math.hypot(p[0] - c[0], p[1] - c[1]) - c[2]) < 1.5) n++;
    if (n > bestCount) {
      bestCount = n;
      best = c;
    }
  }
  if (!best) return null;
  // Algebraic least squares on the inliers.
  let sxx = 0, sxy = 0, syy = 0, sx = 0, sy = 0, sz = 0, sxz = 0, syz = 0, n = 0;
  for (const [x, y] of pts) {
    if (Math.abs(Math.hypot(x - best[0], y - best[1]) - best[2]) >= 1.5) continue;
    const z = x * x + y * y;
    sxx += x * x; sxy += x * y; syy += y * y; sx += x; sy += y; sz += z; sxz += x * z; syz += y * z; n++;
  }
  const sol = solve3(
    [
      [2 * sxx, 2 * sxy, sx],
      [2 * sxy, 2 * syy, sy],
      [2 * sx, 2 * sy, n],
    ],
    [sxz, syz, sz],
  );
  if (!sol) return null;
  const [cx, cy, c0] = sol;
  return { cx: cx * k, cy: cy * k, r: Math.sqrt(c0 + cx * cx + cy * cy) * k, inliers: n / pts.length };
}

function solve3(A: number[][], b: number[]): number[] | null {
  const m = A.map((row, i) => [...row, b[i]]);
  for (let i = 0; i < 3; i++) {
    let p = i;
    for (let j = i + 1; j < 3; j++) if (Math.abs(m[j][i]) > Math.abs(m[p][i])) p = j;
    [m[i], m[p]] = [m[p], m[i]];
    if (Math.abs(m[i][i]) < 1e-12) return null;
    for (let j = 0; j < 3; j++) {
      if (j === i) continue;
      const f = m[j][i] / m[i][i];
      for (let c = i; c < 4; c++) m[j][c] -= f * m[i][c];
    }
  }
  return [m[0][3] / m[0][0], m[1][3] / m[1][1], m[2][3] / m[2][2]];
}
